'use client'

import React, { createContext, useCallback, useContext, useRef, useState } from 'react'

import { getChatsHistory } from '@/api/agent/agentApi'
import type { AgentsThread } from '@/lib/agents-v2-api'

import type { Conversation } from '../components/AgentHistoryPanel'

const STORAGE_KEY = 'aura-agents-panel-open'
const FILES_STORAGE_KEY = 'aura-files-panel-open'
const HISTORY_PAGE_LIMIT = 10

function getStored(): boolean {
  if (typeof window === 'undefined') return true
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'false'
  } catch {
    return true
  }
}

function getFilesStored(): boolean {
  if (typeof window === 'undefined') return true
  try {
    return localStorage.getItem(FILES_STORAGE_KEY) !== 'false'
  } catch {
    return true
  }
}

type AgentHistoryPanelContextType = {
  isAgentsPanelOpen: boolean
  setIsAgentsPanelOpen: (open: boolean) => void
  toggleAgentsPanel: () => void
  isFilesPanelOpen: boolean
  setIsFilesPanelOpen: (open: boolean) => void
  toggleFilesPanel: () => void
  /** Cached list so it survives navigation when clicking a chat; avoids refetch on every open. */
  conversations: Conversation[]
  setConversations: React.Dispatch<React.SetStateAction<Conversation[]>>
  /** Cursor for chat history pagination (null when no more pages). */
  nextCursor: string | null
  setNextCursor: React.Dispatch<React.SetStateAction<string | null>>
  /** Project scope used by the cached history list (null means "all projects"). */
  historyScopeProjectId: string | null
  setHistoryScopeProjectId: React.Dispatch<React.SetStateAction<string | null>>
  /** Refetch the sidebar chat history list (e.g. after creating a new chat / first response). */
  refetchHistory: () => void
  /** Used by AgentHistoryPanel to register its fetch function. */
  registerRefetch: (fn: (() => void) | null) => void
  /** Cached v2 threads list for agent-ide v2. */
  v2Threads: AgentsThread[]
  setV2Threads: React.Dispatch<React.SetStateAction<AgentsThread[]>>
  /** Team scope for cached v2 threads list. */
  v2ThreadsTeamId: string | null
  setV2ThreadsTeamId: React.Dispatch<React.SetStateAction<string | null>>
}

const AgentHistoryPanelContext = createContext<AgentHistoryPanelContextType | undefined>(undefined)

export function AgentHistoryPanelProvider({ children }: { children: React.ReactNode }) {
  const [isAgentsPanelOpen, setOpenState] = useState(getStored)
  const [isFilesPanelOpen, setFilesOpenState] = useState(getFilesStored)
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [historyScopeProjectId, setHistoryScopeProjectId] = useState<string | null>(null)
  const [v2Threads, setV2Threads] = useState<AgentsThread[]>([])
  const [v2ThreadsTeamId, setV2ThreadsTeamId] = useState<string | null>(null)
  const refetchFnRef = useRef<(() => void) | null>(null)
  const fetchingRef = useRef(false)

  const setIsAgentsPanelOpen = useCallback((open: boolean) => {
    setOpenState(open)
    try {
      localStorage.setItem(STORAGE_KEY, String(open))
    } catch {
      // ignore
    }
  }, [])

  const toggleAgentsPanel = useCallback(() => {
    setOpenState((prev) => {
      const next = !prev
      try {
        localStorage.setItem(STORAGE_KEY, String(next))
      } catch {
        // ignore
      }
      return next
    })
  }, [])

  const setIsFilesPanelOpen = useCallback((open: boolean) => {
    setFilesOpenState(open)
    try {
      localStorage.setItem(FILES_STORAGE_KEY, String(open))
    } catch {
      // ignore
    }
  }, [])

  const toggleFilesPanel = useCallback(() => {
    setFilesOpenState((prev) => {
      const next = !prev
      try {
        localStorage.setItem(FILES_STORAGE_KEY, String(next))
      } catch {
        // ignore
      }
      return next
    })
  }, [])

  const refetchHistory = useCallback(async () => {
    if (refetchFnRef.current) {
      refetchFnRef.current()
      return
    }
    if (fetchingRef.current) return
    fetchingRef.current = true
    try {
      const response = await getChatsHistory({ cursor: null, limit: HISTORY_PAGE_LIMIT })
      if (response?.data) {
        const mapped: Conversation[] = response.data.map((chat: any) => ({
          id: chat.id,
          title: chat.title ?? '',
          executions: typeof chat.executions === 'number' ? chat.executions : 0,
          created_at: chat.created_at ?? new Date().toISOString(),
          updated_at: chat.updated_at ?? chat.created_at ?? new Date().toISOString(),
          status: typeof chat.status === 'string' ? chat.status : 'idle',
          organize_label: chat.organize_label === 'pinned' || chat.organize_label === 'archive'
            ? chat.organize_label
            : null
        }))
        setNextCursor(response.next_cursor ?? null)
        setConversations((prev) => {
          // Merge in fresh first-page results:
          // - Update any existing items
          // - Add any new items returned by the server
          // - Preserve any older cached items not in the first page
          const prevById = new Map(prev.map((c) => [c.id, c]))
          const mappedIds = new Set(mapped.map((c) => c.id))
          const mergedFirstPage = mapped.map((fresh) => {
            const existing = prevById.get(fresh.id)
            return existing ? { ...existing, ...fresh } : fresh
          })
          const remainder = prev.filter((c) => !mappedIds.has(c.id))
          return [...mergedFirstPage, ...remainder]
        })
        setHistoryScopeProjectId(null)
      }
    } catch (err) {
      console.error('Failed to refetch chat history:', err)
    } finally {
      fetchingRef.current = false
    }
  }, [])

  const registerRefetch = useCallback((fn: (() => void) | null) => {
    refetchFnRef.current = fn
  }, [])

  return (
    <AgentHistoryPanelContext.Provider
      value={{
        isAgentsPanelOpen,
        setIsAgentsPanelOpen,
        toggleAgentsPanel,
        isFilesPanelOpen,
        setIsFilesPanelOpen,
        toggleFilesPanel,
        conversations,
        setConversations,
        nextCursor,
        setNextCursor,
        historyScopeProjectId,
        setHistoryScopeProjectId,
        refetchHistory,
        registerRefetch,
        v2Threads,
        setV2Threads,
        v2ThreadsTeamId,
        setV2ThreadsTeamId
      }}
    >
      {children}
    </AgentHistoryPanelContext.Provider>
  )
}

export function useAgentHistoryPanel() {
  const ctx = useContext(AgentHistoryPanelContext)
  if (ctx === undefined) {
    throw new Error('useAgentHistoryPanel must be used within AgentHistoryPanelProvider')
  }
  return ctx
}
